
import React, { useEffect, useRef } from "react";

type Props = {
  map: google.maps.Map;
  drawingManager: google.maps.drawing.DrawingManager;
  size?: number;
  position?: google.maps.ControlPosition;
  onClearAll?: () => void;
  drawingEnabled?: boolean;
  host?: HTMLElement | null;

  baseBg?: string;       // very light green bg
  baseBorder?: string;   // soft green border
  iconColor?: string;    // dark green icon
  accent?: string;       // active border color (light green)
  accentRing?: string;   // active ring color (softer green)
};

export default function BigDrawingControls({
  map,
  drawingManager,
  size = 60,
  position = google.maps.ControlPosition.TOP_CENTER,
  onClearAll,
  drawingEnabled = true,
  host,


  baseBg = "#f0fdf4",      // green-50
  baseBorder = "#bbf7d0",  // green-200
  iconColor = "#166534",   // green-700
  accent = "#4ade80",      // green-400 (pleasant light green)
  accentRing = "#bbf7d0",  // green-200
}: Props) {
  const controlDivRef = useRef<HTMLDivElement | null>(null);
  const controlIndexRef = useRef<number | null>(null);
  const overlayListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    if (!map || !drawingManager) return;

    // inject/update CSS once
    const styleId = "big-draw-css";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;

    const css = `
      .gm-big-btn{
        background:${baseBg};
        border:1px solid ${baseBorder};
        border-radius:10px;
        width:${size}px;height:${size}px;
        box-shadow:0 1px 4px rgba(0,0,0,.12);
        display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
        color:${iconColor};
        transition:box-shadow .15s ease, border-color .15s ease, background-color .15s ease;
      }
      .gm-big-btn:hover{
        background:#dcfce7; /* green-100 */
        box-shadow:0 2px 8px rgba(0,0,0,.18)
      }
      .gm-big-btn.active{
        border-color:${accent};
        box-shadow:0 0 0 3px ${accentRing};
      }
      .gm-big-btn svg{width:${Math.round(size * 0.58)}px;height:${Math.round(size * 0.58)}px}
    `;

    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = css;

    // container
    const wrap = document.createElement("div");
    wrap.style.display = "flex";
    wrap.style.gap = host ? "6px" : "10px";
    wrap.style.alignItems = "center";
    wrap.style.margin = host ? "0" : "8px";

    const mkBtn = (title: string, svg: string, onClick: () => void) => {
      const b = document.createElement("button");
      b.className = "gm-big-btn";
      b.type = "button";
      b.title = title;
      b.innerHTML = svg;
      b.addEventListener("click", onClick);
      return b;
    };

    const handSVG =
      `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11V5a1 1 0 1 1 2 0v6h1V4a1 1 0 1 1 2 0v7h1V5a1 1 0 1 1 2 0v6h1V7a1 1 0 1 1 2 0v6c0 3.314-2.686 6-6 6H9a4 4 0 0 1-4-4v-2a2 2 0 1 1 2 0z" fill="currentColor"/></svg>`;
    const polySVG =
      `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7l8-4 8 4v8l-8 4-8-4V7zm8 10l6-3V8l-6-3-6 3v6l6 3z" fill="currentColor"/></svg>`;

      const refreshSVG =
  `<svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.65 6.35A8 8 0 1 0 18 12h-2a6 6 0 1 1-1.76-4.24l-2.12 2.12H20V4l-2.35 2.35z" fill="currentColor"/>
  </svg>`;

    // buttons
    const panBtn = mkBtn("Pan / Move", handSVG, () => {
      drawingManager.setDrawingMode(null);
      setActive(panBtn, polyBtn);
    });

    const polyBtn = mkBtn("Draw polygon", polySVG, () => {
      if (!drawingEnabled) return;
      drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
      setActive(polyBtn, panBtn);
    });
    polyBtn.disabled = !drawingEnabled;
    polyBtn.setAttribute("aria-label", "Draw polygon");
    if (!drawingEnabled) {
      polyBtn.title = "Choose delivery executives and vehicles before drawing";
      polyBtn.style.opacity = "0.45"; polyBtn.style.cursor = "not-allowed";
      drawingManager.setDrawingMode(null);
    }

    const refreshBtn = mkBtn("Refresh", refreshSVG, () => {
  if (onClearAll) onClearAll(); // call parent callback if provided
});


    wrap.appendChild(panBtn);
    wrap.appendChild(polyBtn);
    wrap.appendChild(refreshBtn);

    // push to map controls
    const controls = host ? null : map.controls[position];
    const idx = controls?.getLength() ?? null;
    if (host) host.appendChild(wrap);
    else controls?.push(wrap);
    controlDivRef.current = wrap;
    controlIndexRef.current = idx;

    // active state helpers (same light-green accent for both)
    function setActive(activeEl: HTMLElement, inactiveEl: HTMLElement) {
      activeEl.classList.add("active");
      inactiveEl.classList.remove("active");
    }
    // default: Pan active
    setActive(panBtn, polyBtn);

    // when polygon completes, auto-switch back to Pan
    overlayListenerRef.current = google.maps.event.addListener(
      drawingManager,
      "overlaycomplete",
      () => {
        setActive(panBtn, polyBtn);
        drawingManager.setDrawingMode(null);
      }
    );

    return () => {
      try {
        if (overlayListenerRef.current) {
          google.maps.event.removeListener(overlayListenerRef.current);
          overlayListenerRef.current = null;
        }
      } catch {}

      try {
        if (controls && controlIndexRef.current != null) {
          if (controls.getAt(controlIndexRef.current) === controlDivRef.current) {
            controls.removeAt(controlIndexRef.current);
          } else {
            for (let i = controls.getLength() - 1; i >= 0; i--) {
              if (controls.getAt(i) === controlDivRef.current) {
                controls.removeAt(i);
                break;
              }
            }
          }
        }
      } catch {}
      wrap.remove();

      controlDivRef.current = null;
      controlIndexRef.current = null;
    };
  }, [map, drawingManager, size, position, baseBg, baseBorder, iconColor, accent, accentRing, drawingEnabled, host]);

  return null;
}
